import React, { useState } from 'react';

import { Draggable } from 'react-beautiful-dnd';

import { LoadingButton } from '@atlaskit/button';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import Lozenge from '@atlaskit/lozenge';

import {
  StatusCategorySelect,
  StatusColorSelect,
} from '@qality/common-components';
import { Table } from '@qality/common-components/src/atoms/Table';
import {
  ExecutionStatus,
  ExecutionStatusColor,
  StatusCategory,
} from '@qality/common-model';

import { useTestCaseExecutionStatusPageContext } from '../../TestCaseExecutionStatusPageProvider';
import DeleteStatusModal from '../DeleteStatusModal';
import StatusNameEditor from '../StatusNameEditor';
import {
  ButtonCellPaddingStyled,
  DeleteButtonContainerStyled,
  LozengeCellPaddingStyled,
  SelectCellPaddingStyled,
} from './TestCaseExecutionStatusRow.styled';

type Props = {
  status: ExecutionStatus;
  index: number;
};

export default function TestCaseExecutionEditStatusRow({
  status,
  index,
}: Props): JSX.Element {
  const {
    defaultStatus,
    updateDefaultStatus,
    colors,
    updateStatus,
    loading,
    newStatusId,
    clearNewStatusId,
  } = useTestCaseExecutionStatusPageContext();

  const [isNameValid, setIsNameValid] = useState(true);
  const [statusColor, setStatusColor] = useState<ExecutionStatusColor>(
    status.color
  );
  const [statusCategory, setStatusCategory] = useState<StatusCategory>(
    status.category
  );
  const [statusName, setStatusName] = useState<string>(status.name);
  const [isRowHovered, setIsHovered] = useState(false);
  const [isRowEdited, setIsRowEdited] = useState(false);

  const updateStatusParam = (
    value: Partial<{ name; category; color; isDefault }>
  ): void => {
    const updatedStatus: ExecutionStatus = {
      ...status,
      color: value.color ?? statusColor,
      category: value.category?.toUpperCase() ?? statusCategory.toUpperCase(),
      name: value.name ?? statusName,
      isDefault: value.isDefault ?? defaultStatus.id === status.id,
    };

    if (isUpdatedStatusDifferentFromCurrentStatus(updatedStatus)) {
      updateStatus(updatedStatus);
    }
  };

  const isUpdatedStatusDifferentFromCurrentStatus = (
    updatedStatus: ExecutionStatus
  ) => {
    return (
      updatedStatus.color !== status.color ||
      updatedStatus.category !== status.category ||
      updatedStatus.name !== status.name ||
      updatedStatus.isDefault !== status.isDefault
    );
  };

  return (
    <Draggable
      key={`draggable-status-${status.id}`}
      draggableId={`draggable-status-${status.id}`}
      index={index}
    >
      {(provided, snapshot) => {
        return (
          <Table.DndTr
            data-testid='status-edit-row'
            innerRef={provided.innerRef}
            key={status.id}
            isInvalid={!isNameValid}
            isHoverable={isNameValid}
            isDraggingHighlighted={snapshot.isDragging}
            {...provided.draggableProps}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsRowEdited(true)}
            onBlur={() => setIsRowEdited(false)}
          >
            <Table.DndTd verticalAlign='middle' {...provided.dragHandleProps}>
              <Table.ContentHelper alignItems='center'>
                <DragHandlerIcon label='Drag and drop handler icon' />
              </Table.ContentHelper>
            </Table.DndTd>

            <Table.Td>
              <StatusNameEditor
                onConfirm={(text) => {
                  setStatusName(text);
                  newStatusId && clearNewStatusId();
                  updateStatusParam({ name: text });
                }}
                onCancel={() => {
                  newStatusId && clearNewStatusId();
                }}
                defaultValue={status.name}
                isValid={isNameValid}
                setIsValid={setIsNameValid}
                isDefaultEditing={status.id === newStatusId}
              />
            </Table.Td>
            <Table.Td>
              <SelectCellPaddingStyled>
                <StatusColorSelect
                  options={colors}
                  onChange={(value) => {
                    setStatusColor(value);
                    updateStatusParam({ color: value });
                  }}
                  defaultValue={status.color}
                  isLoading={loading === 'init'}
                />
              </SelectCellPaddingStyled>
            </Table.Td>
            <Table.Td>
              <SelectCellPaddingStyled>
                <StatusCategorySelect
                  onChange={(value) => {
                    setStatusCategory(value);
                    updateStatusParam({ category: value });
                  }}
                  defaultValue={status.category}
                  isLoading={loading === 'init'}
                />
              </SelectCellPaddingStyled>
            </Table.Td>
            <Table.Td>
              {status.id === defaultStatus.id ? (
                <LozengeCellPaddingStyled>
                  <Lozenge>Default</Lozenge>
                </LozengeCellPaddingStyled>
              ) : (
                (isRowHovered || isRowEdited) && (
                  <ButtonCellPaddingStyled>
                    <LoadingButton
                      appearance='subtle'
                      onClick={() => {
                        status.isDefault = true;
                        updateDefaultStatus(status);
                      }}
                      isLoading={loading === 'init'}
                    >
                      Mark as default
                    </LoadingButton>
                  </ButtonCellPaddingStyled>
                )
              )}
            </Table.Td>
            <Table.Td>
              <DeleteButtonContainerStyled>
                <DeleteStatusModal
                  isVisible={isRowHovered || isRowEdited}
                  setIsVisible={(value: boolean) => setIsRowEdited(value)}
                  status={status}
                />
              </DeleteButtonContainerStyled>
            </Table.Td>
          </Table.DndTr>
        );
      }}
    </Draggable>
  );
}
